import React, { createContext, useContext, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Pages that show global/shared resources (not filtered by org)
const GLOBAL_PAGES = [
  '/content',
  '/content/tags',
  '/upload',
  '/settings',
];

interface OrganizationContextType {
  selectedOrgId: string | null; // null means "All Organizations"
  setSelectedOrgId: (id: string | null) => void;
  isGlobalPage: boolean;
  isFiltered: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const STORAGE_KEY = 'justplay_selected_org';

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  // Initialize from localStorage
  const [selectedOrgId, setSelectedOrgIdState] = useState<string | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored || null;
  });

  // Check if current page is a global page (not filtered by org)
  const isGlobalPage = useMemo(() => {
    const path = location.pathname;
    return GLOBAL_PAGES.some(globalPath =>
      path === globalPath || path.startsWith(globalPath + '/')
    );
  }, [location.pathname]);

  // Whether org filtering is active
  const isFiltered = selectedOrgId !== null && !isGlobalPage;

  // Persist to localStorage
  const setSelectedOrgId = (id: string | null) => {
    setSelectedOrgIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  // Clear selection if stored org no longer exists (optional enhancement)
  // This could be done by checking against org list

  const value = useMemo(() => ({
    selectedOrgId,
    setSelectedOrgId,
    isGlobalPage,
    isFiltered,
  }), [selectedOrgId, isGlobalPage, isFiltered]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationContext = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
};

// Helper hook that returns the org ID to use for API filtering
// Returns undefined when on global pages or "All Organizations"
export const useOrgFilter = (): string | undefined => {
  const { selectedOrgId, isGlobalPage } = useOrganizationContext();
  if (isGlobalPage || selectedOrgId === null) {
    return undefined;
  }
  return selectedOrgId;
};

export default OrganizationContext;
